<template>
  <section class="stats-section">
    <div class="stats-container">
      <div class="stats-header">
        <h2 class="stats-title">{{ t('stats.heading') }}</h2>
        <p class="stats-subtitle">{{ t('stats.subheading') }}</p>
      </div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-value">{{ animatedSessions }}</div>
          <div class="stat-label">{{ t('stats.completed_sessions') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ animatedTrainers }}+</div>
          <div class="stat-label">{{ t('stats.online_trainers') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ animatedTrainees }}</div>
          <div class="stat-label">{{ t('stats.registered_trainees') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ animatedSatisfaction }}%</div>
          <div class="stat-label">{{ t('stats.satisfaction_rate') }}</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useLanguage } from '@/composables/useLanguage'

const { t } = useLanguage()

const animatedSatisfaction = ref('0');
const animatedTrainees = ref('0');
const animatedTrainers = ref('0');
const animatedSessions = ref('0');

const animateValue = (start, end, duration, callback, isDecimal = false) => {
  const range = end - start;
  const increment = range / (duration / 16);
  let current = start;

  const timer = setInterval(() => {
    current += increment;
    if (current >= end) {
      current = end;
      clearInterval(timer);
    }
    if (isDecimal) {
      callback(current.toFixed(1));
    } else {
      callback(Math.floor(current).toLocaleString('en-US'));
    }
  }, 16);
};

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Start animations
          animateValue(0, 98.4, 2000, (val) => {
            animatedSatisfaction.value = val;
          }, true);

          animateValue(0, 22850, 2000, (val) => {
            animatedTrainees.value = val;
          });

          animateValue(0, 240, 2000, (val) => {
            animatedTrainers.value = val;
          });

          animateValue(0, 2441506, 2000, (val) => {
            animatedSessions.value = val;
          });

          observer.disconnect();
        }
      });
    },
    { threshold: 0.3 }
  );

  const section = document.querySelector('.stats-section');
  if (section) {
    observer.observe(section);
  }
});
</script>

<style scoped>
/* Main Section */
.stats-section {
  width: 100%;
  position: relative;
  background: var(--Color-6, #082C40);
  padding: clamp(30px, 5vw, 60px) clamp(20px, 5vw, 80px);
  overflow: hidden;
}

.stats-section::before,
.stats-section::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: #27B4FF;
  opacity: 0.4;
  filter: blur(370px);
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 0;
}

.stats-section::before {
  left: -300px;
}

.stats-section::after {
  right: -300px;
}

.stats-container {
  position: relative;
  z-index: 1;
}

/* Container */
.stats-container {
  max-width: 1760px;
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: clamp(30px, 4vw, 60px);
}

/* Header */
.stats-header {
  text-align: start;
  flex-shrink: 0;
  max-width: 360px;
}

.stats-title {
  margin: 0 0 clamp(10px, 1.5vw, 16px) 0;
  font-size: clamp(22px, 2.4vw, 36px);
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
}

.stats-subtitle {
  margin: 0;
  font-size: clamp(12px, 0.9vw, 14px);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.7);
}

/* Stats Grid */
.stats-grid {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: clamp(20px, 3vw, 40px);
  flex: 1;
  padding: 0 clamp(20px, 3vw, 60px);
}

/* Stat Cards */
.stat-card {
  background: transparent;
  border-radius: 0;
  padding: 0;
  text-align: center;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: clamp(8px, 1vw, 12px);
  min-height: auto;
}

.stat-card:hover {
  transform: none;
  box-shadow: none;
}

.stat-value {
  font-size: clamp(28px, 3vw, 44px);
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.stat-label {
  font-size: clamp(13px, 1vw, 16px);
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.3;
}

/* RTL Support */
html[lang="ar"] .stats-section {
  font-family: 'Cairo', sans-serif;
  direction: rtl;
}

html[lang="ar"] .stats-title {
  font-size: clamp(26px, 4.5vw, 44px);
}

html[lang="ar"] .stat-value {
  font-family: 'Poppins', sans-serif; /* Numbers stay in English font */
}

/* Tablet & Mobile */
@media (max-width: 992px) {
  .stats-section {
    padding: clamp(30px, 6vw, 50px) clamp(15px, 4vw, 30px);
  }

  .stats-container {
    flex-direction: column;
    align-items: center;
    gap: clamp(24px, 4vw, 36px);
  }

  .stats-header {
    text-align: center;
    max-width: 600px;
  }

  .stats-grid {
    width: 100%;
    justify-content: space-around;
    flex-wrap: wrap;
    gap: clamp(20px, 4vw, 40px);
  }
}

@media (max-width: 480px) {
  .stats-section {
    padding: clamp(24px, 5vw, 36px) clamp(12px, 3vw, 20px);
  }

  .stats-title {
    font-size: clamp(20px, 5vw, 28px);
  }

  .stats-subtitle {
    font-size: clamp(13px, 3vw, 15px);
  }

  .stats-grid {
    gap: clamp(16px, 3vw, 24px);
  }

  .stat-value {
    font-size: clamp(24px, 6vw, 36px);
  }

  .stat-label {
    font-size: clamp(12px, 2.5vw, 14px);
  }
}
</style>
